/** User record editor — one person (`users.User`), read and edited in place on the
 *  records core the booking and order records run on (plan
 *  2026-10-03-console-user-record-page.md, D1, D6).
 *
 *  - `GET /users/edit/catalog/` — the static field registry (card, widget, the
 *    permissions and guards a change needs, what an import does to each field), the
 *    effect catalogue, the choice questions (`user_email` / `user_phone`: required, no
 *    default, raised by a contacts change that removes or disables the user's own email /
 *    phone contact) and the kill switch. Cached for an hour.
 *  - `GET /users/{pk}/edit_context/` — the per-user facts: `baselines` (the `from` of
 *    every change), the guards that block a field for this caller (`blocked`), the
 *    caller's permissions, whether the user is soft-deleted, and the feed verdicts over
 *    the user's open bookings (`restech`).
 *  - `POST /users/{pk}/changeset/preview/` — plans a changeset, writes nothing.
 *  - `POST /users/{pk}/changeset/` — applies it in one transaction. A 409 rejects as
 *    `UserDriftError`, a 400 as `UserChangesetValidationError`, and the field-keyed 403
 *    (a PII field without console_user_view, Django admin access without
 *    console_perm_grant) as one too (`code: forbidden`).
 *
 *  Feed verdicts come from edit_context (`restech.verdicts`) and the PREVIEW's diff rows;
 *  the apply's diff rows may carry `verdict: null` (verdicts are not computed under its
 *  row lock), so nothing reads a verdict from an apply result.
 *
 *  Email and phone are not fields: the `contacts` field (UserRecordContacts) is the
 *  user's contact list plus which rows are the user's own email and phone.
 *
 *  Hand-authored (console convention); mirrors `UserRecord*` in console.openapi.yaml
 *  (contract test: userRecord.contract.test.ts).
 */
import {
  keepPreviousData,
  useMutation,
  useQuery,
  useQueryClient,
  type UseMutationResult,
  type UseQueryResult,
} from '@tanstack/react-query';

import type { RecordChangeset, RecordPreview } from '@/features/records/editor/types';

import type { BookingEditCatalogEffect, BookingEditEffectSummary } from './bookingEdit';
import type {
  BookingRecordChangedBy,
  BookingRecordFollowUp,
  BookingRecordPlannedStep,
  BookingRecordRequiredChoice,
} from './bookingRecord';
import type { BookingTenantRef } from './bookings';
import { apiGet, apiPost, ConsoleApiError } from './client';
import { userDetailKey, type ConsoleUserDetail, type UserKind } from './users';

// ---------------------------------------------------------------------------
// Vocabulary
// ---------------------------------------------------------------------------

/** The person record's cards, in page order (UserRecordSection). */
export const USER_RECORD_SECTIONS = ['contacts', 'reachability', 'identity', 'access', 'origin', 'technical'] as const;
export type UserRecordSection = (typeof USER_RECORD_SECTIONS)[number];

/** The editable fields — `UserRecordChange.field`, in catalog order. */
export const USER_RECORD_EDITABLE_KEYS = [
  'contacts',
  'freeze_email',
  'freeze_phone',
  'allow_email_nf',
  'allow_sms_nf',
  'allow_whatsapp_nf',
  'first_name',
  'last_name',
  'lang',
  'country',
  'date_of_birth',
  'passport_number',
  'address',
  'tags',
  'is_active',
  'is_staff',
] as const;
export type UserRecordEditableKey = (typeof USER_RECORD_EDITABLE_KEYS)[number];

export type UserRecordFieldKind = 'editable' | 'vendor' | 'derived';

export type UserRecordWidget = 'text' | 'textarea' | 'enum' | 'country' | 'date' | 'boolean' | 'tags' | 'contacts';

/** What an import does to a user field, statically. */
export type UserRecordRule = 'when_sent' | 'conditional' | 'by_category' | 'never';

/** Why a field is read-only on one user (catalog `guards`, edit_context `blocked`). */
export type UserRecordGuard = 'not_guest' | 'not_self';

export type UserRecordOptionKey = 'update_events' | 'user_email' | 'user_phone';

/** `choice` — one of `values`; `contact` — the key of an enabled contact row of the
 *  changeset's contacts value (or one of `values`: user_phone `none`). */
export type UserRecordOptionType = 'choice' | 'contact';

export type UserRecordVerdictCode = 'overwrites' | 'may_overwrite' | 'kept' | 'suspended';

export type UserRecordErrorCode =
  | 'disabled'
  | 'invalid_request'
  | 'too_many_changes'
  | 'entity_not_allowed'
  | 'unknown_field'
  | 'duplicate_change'
  | 'not_editable'
  | 'invalid_value'
  | 'required_choice'
  | 'email_in_use'
  | 'description_too_long'
  | 'forbidden';

/** `user_phone`'s answer for "the user keeps no phone". */
export const USER_PHONE_NONE = 'none';

// ---------------------------------------------------------------------------
// The contacts field (UserRecordContacts)
// ---------------------------------------------------------------------------

export type UserContactType = 'email' | 'phone' | 'url';
/** `auto` (a `to` only) — the importer's heuristics decide. */
export type UserContactTier = 'private' | 'ota' | 'fake' | 'auto';

export interface UserRecordContact {
  /** `"contact:<id>"` for a stored row; any other unique string for a new one. */
  key: string;
  /** Null for a contact the change adds. */
  id: number | null;
  type: UserContactType;
  contact: string;
  tier: UserContactTier;
  verified: boolean;
  /** Notifications allowed to this contact. */
  enabled: boolean;
}

/** The `contacts` field's wire value: the list (by type, then sequence) and which rows
 *  are the user's own email and phone (null: not a contact / none). */
export interface UserRecordContacts {
  contacts: UserRecordContact[];
  user_email: string | null;
  user_phone: string | null;
}

export type UserRecordContactDriftReason = 'changed' | 'deleted' | 'user_email' | 'user_phone';

export interface UserRecordContactDrift {
  key: string | null;
  id: number | null;
  reason: UserRecordContactDriftReason;
}

// ---------------------------------------------------------------------------
// Catalog
// ---------------------------------------------------------------------------

export interface UserRecordChoice {
  value: number | string;
  label: string;
}

export interface UserRecordCatalogField {
  /** Unique across the catalog (UserRecordFieldKey); the `field` of a change. */
  key: string;
  entity: 'user';
  label: string;
  kind: UserRecordFieldKind;
  section: UserRecordSection;
  /** The edit-mode input (kind = editable); null for the read-only kinds. */
  widget: UserRecordWidget | null;
  choices: UserRecordChoice[];
  nullable: boolean;
  max_length?: number | null;
  /** Every console permission a change needs. */
  permissions: string[];
  guards: UserRecordGuard[];
  /** Guest pre-check-in data (date of birth, passport, address). */
  pii: boolean;
  tenant_scoped: boolean;
  effects: string[];
  automatic: string[];
  restech_rule: UserRecordRule | null;
  restech_note: string;
  /** vendor / derived kinds: the users-detail properties the field renders (dotted). */
  detail_keys: string[];
  help?: string;
  warning?: string;
}

export interface UserRecordCatalogOption {
  key: UserRecordOptionKey;
  /** events.update_events, options.user_email, options.user_phone. */
  path: string;
  label: string;
  help?: string;
  type: UserRecordOptionType;
  values: Array<{ value: string; label: string; warning?: string }>;
  /** update_events: suppress; null for the contacts questions. */
  default: string | null;
  required: boolean;
  asked_when: string[];
  asked_when_note?: string;
}

export interface UserRecordCatalog {
  /** The ConsoleUserEditEnabled kill switch. */
  enabled: boolean;
  max_changes: number;
  description_max_length: number;
  event_tag_template: string;
  fields: UserRecordCatalogField[];
  effects: BookingEditCatalogEffect[];
  options: UserRecordCatalogOption[];
  /** UserRecordGuard → explanation. */
  guards: Record<string, string>;
  /** UserRecordRule → explanation. */
  restech_rules: Record<string, string>;
}

// ---------------------------------------------------------------------------
// Edit context
// ---------------------------------------------------------------------------

export interface UserRecordVerdict {
  verdict: UserRecordVerdictCode;
  evidence: string;
  /** The evaluated open bookings by their verdict for this field. */
  bookings: { overwrites: number[]; may_overwrite: number[]; suspended: number[]; kept: number[] };
}

export interface UserRecordRestechBooking {
  booking_id: number;
  lead_booking_id: number | null;
  tour: { id: number; name: string | null } | null;
  start_ts: string | null;
  phase: 'active' | 'future';
  managed_by: string | null;
  managed_by_display: string | null;
  account: string | null;
  source: {
    source_id?: string | null;
    origin?: string | null;
    import_run_id?: string | null;
    last_run_ts?: string | null;
  } | null;
  mode: 'full' | 'append' | null;
  permissions: { update_bookings: boolean | null; update_users: boolean | null };
  fingerprint_ts: string | null;
  fingerprint_status: 'recorded' | 'missing';
  suspended_reason: 'checkin_local' | 'checkin_dashboard' | 'checkin_console' | null;
  /** The feed writes this user's profile through the booking. */
  writes_user: boolean;
}

/** What the next feed import does to each field, over the user's open bookings. */
export interface UserRecordRestech {
  /** Most open bookings evaluated (25). */
  cap: number;
  evaluated: number;
  /** Open bookings beyond the cap, not evaluated. */
  more: number;
  bookings: UserRecordRestechBooking[];
  /** Editable field key → its aggregated verdict. */
  verdicts: Record<string, UserRecordVerdict>;
}

export interface UserRecordEditContext {
  user_id: number;
  generated_ts: string;
  flags: { user_edit_enabled: boolean };
  /** The caller's console permissions by codename. */
  permissions: {
    console_user_manage: boolean;
    console_user_view: boolean;
    console_perm_grant: boolean;
    [codename: string]: boolean;
  };
  kind: UserKind;
  /** A soft-deleted user is read-only until restored. */
  is_deleted: boolean;
  tenant: BookingTenantRef | null;
  /** `"user:<id>"` → field key → current value, in the changeset wire format. */
  baselines: Record<string, Record<string, unknown>>;
  /** `"user:<id>:<field>"` → the guards keeping it read-only for this caller. */
  blocked: Record<string, UserRecordGuard[]>;
  restech: UserRecordRestech;
}

// ---------------------------------------------------------------------------
// Changeset (request)
// ---------------------------------------------------------------------------

export type UserUpdateEvents = 'emit' | 'suppress';

export interface UserRecordChange {
  entity: 'user';
  id: number;
  field: UserRecordEditableKey;
  from: unknown;
  to: unknown;
}

export interface UserRecordChangeset {
  description?: string | null;
  events?: { update_events?: UserUpdateEvents };
  /** The contacts questions' answers: a contact row key (user_phone: or `none`). */
  options?: { user_email?: string | null; user_phone?: string | null };
  changes: UserRecordChange[];
}

/** The records core's changeset as this endpoint takes it: field changes of the user
 *  (a user takes no actions), the event policy and the contacts questions' answers. */
export function toUserChangeset(body: RecordChangeset): UserRecordChangeset {
  const events = body.events as { update_events?: UserUpdateEvents } | undefined;
  const options = body.options as { user_email?: unknown; user_phone?: unknown } | undefined;
  const out: UserRecordChangeset = {
    changes: body.changes.flatMap((c) =>
      c.field ? [{ entity: 'user' as const, id: c.id, field: c.field as UserRecordEditableKey, from: c.from, to: c.to }] : [],
    ),
  };
  if (body.description) out.description = body.description;
  if (events?.update_events) out.events = { update_events: events.update_events };
  const answers: NonNullable<UserRecordChangeset['options']> = {};
  for (const k of ['user_email', 'user_phone'] as const) {
    const v = options?.[k];
    if (typeof v === 'string') answers[k] = v;
  }
  if (Object.keys(answers).length > 0) out.options = answers;
  return out;
}

// ---------------------------------------------------------------------------
// Preview / apply (responses)
// ---------------------------------------------------------------------------

export interface UserRecordChangeOutcome {
  /** `"user:<id>:<field>"`. */
  key: string;
  entity: 'user';
  id: number;
  field: string;
  outcome: 'change' | 'no_change';
  from: unknown;
  to: unknown;
  from_display?: string | null;
  to_display?: string | null;
  /** Preview: what the next import does to the value as the save leaves it. Apply: may
   *  be null — read verdicts from the preview or edit_context. */
  verdict?: UserRecordVerdict | null;
  notes?: string[];
}

export interface UserRecordConflict {
  code: 'drift' | 'email_in_use';
  key: string;
  detail: string;
  current?: unknown;
  changed_by?: BookingRecordChangedBy | null;
  contacts?: UserRecordContactDrift[];
  owner_user_id?: number | null;
  contact_key?: string | null;
}

export interface UserRecordEvent {
  code: string;
  entity: 'user';
  id: number;
  status: string;
  guest_visible: boolean;
  tag?: string | null;
  logid?: string | null;
  event_delivery_uuid?: string | null;
}

export interface UserRecordPreview {
  changes: UserRecordChangeOutcome[];
  required_choices: BookingRecordRequiredChoice[];
  conflicts: UserRecordConflict[];
  effects: Record<string, BookingEditEffectSummary>;
  events: UserRecordEvent[];
  external: BookingRecordPlannedStep[];
  warnings: string[];
}

export interface UserRecordApplyResult {
  audit_id: number;
  audit_uuid: string;
  event_tag: string;
  saved_ts: string;
  changes: UserRecordChangeOutcome[];
  events: UserRecordEvent[];
  follow_ups: BookingRecordFollowUp[];
  warnings: string[];
  /** Re-seed the page from these two (null only when re-reading failed; the save stands). */
  detail: ConsoleUserDetail | null;
  edit_context: UserRecordEditContext | null;
}

/** A user plan as the ledger reads it: its follow-ups are the effects it flushes. */
export function toRecordPreview(p: UserRecordPreview): RecordPreview {
  return { ...p, follow_ups: [] };
}

// ---------------------------------------------------------------------------
// Errors
// ---------------------------------------------------------------------------

export interface UserRecordDriftedField {
  key: string;
  entity: 'user';
  id: number;
  field: string;
  current: unknown;
  changed_by?: BookingRecordChangedBy | null;
  /** contacts: each contact that changed or went, and the user email / phone moving. */
  contacts?: UserRecordContactDrift[];
}

export interface UserRecordFieldError {
  code: UserRecordErrorCode;
  message: string;
  guard?: UserRecordGuard | null;
  /** A contacts error: the row it concerns. */
  contact_key?: string | null;
  /** email_in_use: the user who already has the address. */
  owner_user_id?: number | null;
}

/** 409 of the apply: some `from` no longer matches. Nothing was written. */
export class UserDriftError extends ConsoleApiError {
  constructor(
    status: number,
    body: ConsoleApiError['body'],
    message: string,
    public readonly drifted: UserRecordDriftedField[],
  ) {
    super(status, body, message);
  }
}

/** 400 of the preview or apply — and the field-keyed 403 (`code: forbidden`, `missing`
 *  the permissions lacked). `errors` is keyed `"user:<id>:<field>"`, `"options.<key>"`,
 *  `"events.update_events"`, `"description"` or `"changes"`. Nothing was written. */
export class UserChangesetValidationError extends ConsoleApiError {
  constructor(
    status: number,
    body: ConsoleApiError['body'],
    message: string,
    public readonly code: UserRecordErrorCode,
    public readonly errors: Record<string, UserRecordFieldError[]>,
    public readonly missing: string[] = [],
  ) {
    super(status, body, message);
  }
}

function isPlainObject(v: unknown): v is Record<string, unknown> {
  return typeof v === 'object' && v !== null && !Array.isArray(v);
}

/** Re-type a changeset call's ConsoleApiError by its body; anything else passes through.
 *  A 403 without a field-keyed body (no console_user_manage) stays a plain error. */
export function toUserChangesetError(e: unknown): unknown {
  if (!(e instanceof ConsoleApiError)) return e;
  const body: unknown = e.body;
  if (!isPlainObject(body)) return e;
  if (e.status === 409 && body.code === 'drift' && Array.isArray(body.drifted)) {
    return new UserDriftError(e.status, e.body, e.message, body.drifted as UserRecordDriftedField[]);
  }
  if ((e.status === 400 || e.status === 403) && typeof body.code === 'string' && isPlainObject(body.errors)) {
    return new UserChangesetValidationError(
      e.status,
      e.body,
      e.message,
      body.code as UserRecordErrorCode,
      body.errors as Record<string, UserRecordFieldError[]>,
      Array.isArray(body.missing) ? (body.missing as string[]) : [],
    );
  }
  return e;
}

// ---------------------------------------------------------------------------
// Calls
// ---------------------------------------------------------------------------

export function fetchUserRecordCatalog(): Promise<UserRecordCatalog> {
  return apiGet<UserRecordCatalog>('/users/edit/catalog/');
}

export function fetchUserEditContext(id: number, signal?: AbortSignal): Promise<UserRecordEditContext> {
  return apiGet<UserRecordEditContext>(`/users/${id}/edit_context/`, signal ? { signal } : undefined);
}

export async function previewUserChangeset(
  id: number,
  body: UserRecordChangeset,
  signal?: AbortSignal,
): Promise<UserRecordPreview> {
  try {
    return await apiPost<UserRecordPreview>(`/users/${id}/changeset/preview/`, body, signal ? { signal } : undefined);
  } catch (e) {
    throw toUserChangesetError(e);
  }
}

export async function applyUserChangeset(id: number, body: UserRecordChangeset): Promise<UserRecordApplyResult> {
  try {
    return await apiPost<UserRecordApplyResult>(`/users/${id}/changeset/`, body);
  } catch (e) {
    throw toUserChangesetError(e);
  }
}

// ---------------------------------------------------------------------------
// React Query
// ---------------------------------------------------------------------------

/** The catalog only changes with a deploy. */
export const USER_RECORD_CATALOG_STALE_MS = 60 * 60_000;

export const userRecordKeys = {
  all: ['userRecord'] as const,
  catalog: () => ['userRecord', 'catalog'] as const,
  editContext: (id: number) => ['userRecord', 'editContext', id] as const,
  preview: (id: number, body: UserRecordChangeset) => ['userRecord', 'preview', id, body] as const,
};

/** The user's contacts list (OTA name, created / updated) — what the save re-reads. */
export const userContactsKey = (id: number) => ['userContacts', id] as const;

export function useUserRecordCatalog(): UseQueryResult<UserRecordCatalog> {
  return useQuery({
    queryKey: userRecordKeys.catalog(),
    queryFn: fetchUserRecordCatalog,
    staleTime: USER_RECORD_CATALOG_STALE_MS,
  });
}

export function useUserEditContext(
  id: number,
  { enabled = true }: { enabled?: boolean } = {},
): UseQueryResult<UserRecordEditContext> {
  return useQuery({
    queryKey: userRecordKeys.editContext(id),
    queryFn: ({ signal }) => fetchUserEditContext(id, signal),
    enabled: enabled && Number.isFinite(id),
  });
}

/** The ledger's preview of a (debounced) compiled draft; `null` = nothing to preview —
 *  keyed by the body, the previous plan kept on screen while the next loads. */
export function useUserChangesetPreview(id: number, body: RecordChangeset | null): UseQueryResult<RecordPreview> {
  const wire = body ? toUserChangeset(body) : { changes: [] };
  return useQuery({
    queryKey: userRecordKeys.preview(id, wire),
    queryFn: ({ signal }) => previewUserChangeset(id, wire, signal),
    select: toRecordPreview,
    enabled: body !== null && wire.changes.length > 0 && Number.isFinite(id),
    staleTime: 0,
    gcTime: 60_000,
    retry: false,
    placeholderData: keepPreviousData,
  });
}

/** Save. `onSaved` runs first (the draft resets before the re-seeded baselines rebase
 *  it), then the edit context and the users detail are re-seeded from the response —
 *  refetched instead when the response could not re-read them — and the contacts list
 *  is refetched. A 409 rejects with `UserDriftError` (the edit context is refetched for
 *  the rebase), a 400 / field-keyed 403 with `UserChangesetValidationError`. */
export function useApplyUserChangeset(
  id: number,
  { onSaved }: { onSaved?: (result: UserRecordApplyResult) => void } = {},
): UseMutationResult<UserRecordApplyResult, unknown, RecordChangeset> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: RecordChangeset) => applyUserChangeset(id, toUserChangeset(body)),
    onSuccess: (result) => {
      onSaved?.(result);
      if (result.edit_context) qc.setQueryData(userRecordKeys.editContext(id), result.edit_context);
      else void qc.invalidateQueries({ queryKey: userRecordKeys.editContext(id) });
      if (result.detail) qc.setQueryData(userDetailKey(id), result.detail);
      else void qc.invalidateQueries({ queryKey: userDetailKey(id) });
      void qc.invalidateQueries({ queryKey: userContactsKey(id) });
    },
    onError: (e) => {
      if (e instanceof UserDriftError) {
        void qc.invalidateQueries({ queryKey: userRecordKeys.editContext(id) });
      }
    },
  });
}
